import { DayOfWeek, LocalDate, TemporalAdjusters } from "@js-joda/core";
import { useMemo, useState } from "react";
import type { RefObject } from "react";
import { ScrollSyncProvider, useScrollSyncGroup } from "hooks/useScrollSync";
import { useTranslation } from "react-i18next";
import { WeekView } from "./scheduleWeekView.styled";
import { ScheduleWeekHeaderRow } from "./scheduleWeekHeaderRow.component";
import {
  ScheduleRow,
  ScheduleCreatePayload,
  ShiftClickPayload,
} from "../../common/scheduleRow/scheduleRow.component";
import { ScheduleWorkerRow } from "web-apps/company/components/schedule/types/schedule.types";
import { ScheduleViewBody } from "../shared/scheduleViewBody.component";
import {
  formatDateKey,
  formatItemDateKey,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { GroupByOption } from "web-apps/company/components/schedule/types/schedule.types";
import { GroupDivider } from "../shared/groupDivider.component";
import { getRowRenderKey } from "../shared/scheduleRow.key.logic";
import { ScheduleDayShiftsPanel } from "web-apps/company/components/schedule/components/common/dayShiftsPanel/scheduleDayShiftsPanel.component";

interface Props {
  currentDate: LocalDate;
  onChangeDate: (nextDate: LocalDate) => void;
  shiftBlockSize: "small" | "large" | "pill";
  isLoading: boolean;
  isLoadingMore: boolean;
  isEmpty: boolean;
  emptyText: string;
  groupByOption: GroupByOption;
  workerRows: ScheduleWorkerRow[];
  onShiftClick: (payload: ShiftClickPayload) => void;
  onScheduleClick: (payload: ScheduleCreatePayload) => void;
  loadMoreRef: RefObject<HTMLDivElement>;
}

export const ScheduleWeekView: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { register } = useScrollSyncGroup();
  const [selectedDay, setSelectedDay] = useState<LocalDate | null>(null);

  const weekStart = useMemo(
    () =>
      props.currentDate.with(
        TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY),
      ),
    [props.currentDate],
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => weekStart.plusDays(index)),
    [weekStart],
  );
  const weekDayKeys = useMemo(
    () => new Set(weekDays.map((date) => formatDateKey(date))),
    [weekDays],
  );

  const visibleWorkerRows = useMemo(
    () =>
      props.workerRows.filter((workerRow) =>
        workerRow.sections.some((section) =>
          section.items.some((item) =>
            weekDayKeys.has(formatItemDateKey(item.startTime)),
          ),
        ),
      ),
    [props.workerRows, weekDayKeys],
  );
  const isViewEmpty = !props.isLoading && visibleWorkerRows.length === 0;
  const rowsWithDividers = useMemo(() => {
    if (props.groupByOption !== "team") {
      return visibleWorkerRows.map((workerRow) => ({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: null as JSX.Element | null,
      }));
    }

    const result: Array<{
      key: string;
      row: ScheduleWorkerRow;
      divider: JSX.Element | null;
    }> = [];
    let lastWorkGroupId: number | null = null;
    visibleWorkerRows.forEach((workerRow) => {
      const section = workerRow.sections[0];
      const workGroupId = section?.id ?? null;
      const rawUnitName = (section?.unitName ?? "").trim();
      const unitName =
        rawUnitName.length > 0 ? rawUnitName : t("Schedule.UnknownUnit");
      const rawTeamName = (section?.text ?? "").trim();
      const teamName =
        rawTeamName.length > 0 ? rawTeamName : t("Schedule.UnknownTeam");
      const shouldRenderDivider =
        workGroupId !== null && workGroupId !== lastWorkGroupId;
      result.push({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: shouldRenderDivider ? (
          <GroupDivider
            key={`divider-${workGroupId}`}
            unitName={unitName}
            teamName={teamName}
          />
        ) : null,
      });
      lastWorkGroupId = workGroupId;
    });
    return result;
  }, [props.groupByOption, t, visibleWorkerRows]);

  return (
    <ScrollSyncProvider value={register}>
      <WeekView>
        <ScheduleWeekHeaderRow
          currentDate={props.currentDate}
          onChangeDate={props.onChangeDate}
          weekDays={weekDays}
          onDayClick={setSelectedDay}
        />
        <ScheduleViewBody
          isLoading={props.isLoading}
          isEmpty={props.isEmpty || isViewEmpty}
          emptyText={props.emptyText}
          isLoadingMore={props.isLoadingMore}
          loadMoreRef={props.loadMoreRef}
          hasContent={visibleWorkerRows.length > 0}
          isDimmed={props.isLoading && visibleWorkerRows.length > 0}
        >
          {rowsWithDividers.map((entry) => (
            <div key={entry.key}>
              {entry.divider}
              <ScheduleRow
                workerRow={entry.row}
                days={weekDays}
                shiftBlockSize={props.shiftBlockSize}
                onShiftClick={props.onShiftClick}
                onScheduleClick={props.onScheduleClick}
              />
            </div>
          ))}
        </ScheduleViewBody>
      </WeekView>
      <ScheduleDayShiftsPanel
        date={selectedDay}
        onClose={() => setSelectedDay(null)}
      />
    </ScrollSyncProvider>
  );
};
